import { DiagnosticsStore } from "./diagnosticsStore.ts";
import { DocumentSync, type SyncDoc } from "./documentSync.ts";
import { JsonRpcPeer } from "./jsonrpc.ts";
import { CLIENT_CAPABILITIES, LSP_REQUEST_TIMEOUT_MS } from "./capabilities.ts";
import { LSP_SYNTHETIC_SCHEME, lspLanguageIdForPath, type LspTransport, type LspWorkspaceEdit } from "./types.ts";

export type LspClientState = "idle" | "starting" | "ready" | "unavailable" | "crashed" | "stopped";

export interface LspStartOutcome {
  transport: LspTransport;
  state: "ready" | "unavailable";
  reason?: string;
}

export interface LspClientOptions {
  projectId: string;
  projectName: string;
  /** Starts a fresh Agent-side session (new session id) and returns its transport. */
  start: () => Promise<LspStartOutcome>;
  /** workspace/applyEdit (server-initiated, e.g. code-action commands). Must stay Project-bounded. */
  applyEdit?: (edit: LspWorkspaceEdit) => Promise<boolean>;
  onState?: (state: LspClientState, message?: string) => void;
  /** Coalesces rapid edits into one didChange. */
  debounceMs?: number;
}

/**
 * One language-server connection for one Project (M6.1). Monaco-free on purpose: the editor bindings (providers.ts) and
 * the hook sit on top. A restart / reconnect ALWAYS creates a fresh session and re-opens every attached document exactly
 * once, so the server never sees a duplicate stream. LSP is not durable.
 */
export class LspClient {
  readonly diagnostics: DiagnosticsStore;
  private readonly opts: LspClientOptions;
  private state: LspClientState = "idle";
  private peer: JsonRpcPeer | null = null;
  private transport: LspTransport | null = null;
  private sync: DocumentSync | null = null;
  private generation = 0;
  private readonly docs = new Map<string, SyncDoc>();
  private readonly pending = new Map<string, ReturnType<typeof setTimeout>>();
  private disposed = false;

  constructor(opts: LspClientOptions) {
    this.opts = opts;
    this.diagnostics = new DiagnosticsStore(opts.projectId);
  }

  get currentState(): LspClientState {
    return this.state;
  }

  /** Register a document (a Monaco model of an open tab). Starts the server on the first TS/JS document. */
  attach(doc: SyncDoc): void {
    if (this.disposed || lspLanguageIdForPath(doc.path) === null) return;
    this.docs.set(doc.path, doc);
    if (this.state === "ready") this.sync?.open(doc);
    else if (this.state === "idle" || this.state === "stopped") void this.startSession();
  }

  changed(path: string): void {
    const doc = this.docs.get(path);
    if (!doc || this.state !== "ready") return;
    const prev = this.pending.get(path);
    if (prev) clearTimeout(prev);
    this.pending.set(
      path,
      setTimeout(() => this.flushPath(path), this.opts.debounceMs ?? 40),
    );
  }

  saved(path: string): void {
    const doc = this.docs.get(path);
    if (!doc || this.state !== "ready") return;
    this.flushPath(path); // the server must have the final text before didSave
    this.sync?.save(doc);
  }

  detach(path: string): void {
    if (!this.docs.delete(path)) return;
    const t = this.pending.get(path);
    if (t) clearTimeout(t);
    this.pending.delete(path);
    this.sync?.close(path);
    this.diagnostics.publish({ uri: `${LSP_SYNTHETIC_SCHEME}://${this.opts.projectId}/${path}`, diagnostics: [] });
  }

  /** Sends every pending edit now (call before any request so answers reflect the unsaved buffer). */
  flush(): void {
    for (const path of [...this.pending.keys()]) this.flushPath(path);
  }

  async request<T>(method: string, params: unknown, signal?: AbortSignal): Promise<T | null> {
    const peer = this.peer;
    if (!peer || this.state !== "ready") return null;
    this.flush();
    return peer.request<T>(method, params, { timeoutMs: LSP_REQUEST_TIMEOUT_MS, ...(signal ? { signal } : {}) });
  }

  /** Fresh session (after a crash, a reconnect, or a manual restart). Never loops by itself. */
  async restart(): Promise<void> {
    if (this.disposed) return;
    this.teardown("restart");
    this.state = "idle"; // a start in flight is superseded (its generation is stale)
    if (this.docs.size > 0) await this.startSession();
    else this.setState("idle");
  }

  /** The connection to the server was lost (socket reconnect): the Agent drops our session; start a fresh one. */
  connectionLost(): void {
    if (this.disposed || this.state === "idle") return;
    void this.restart();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.teardown("disposed");
    this.docs.clear();
    this.setState("stopped");
  }

  // ---- internals ---------------------------------------------------------------------------------------

  private flushPath(path: string): void {
    const t = this.pending.get(path);
    if (t) clearTimeout(t);
    this.pending.delete(path);
    const doc = this.docs.get(path);
    if (doc && this.state === "ready") this.sync?.change(doc);
  }

  private setState(state: LspClientState, message?: string): void {
    this.state = state;
    this.opts.onState?.(state, message);
  }

  private teardown(reason: string): void {
    this.generation++;
    for (const t of this.pending.values()) clearTimeout(t);
    this.pending.clear();
    const peer = this.peer;
    const transport = this.transport;
    this.peer = null;
    this.transport = null;
    this.sync?.reset();
    this.sync = null;
    peer?.dispose(reason);
    transport?.close(); // stops the Agent-side process (lsp.stop)
    this.diagnostics.clear();
  }

  private async startSession(): Promise<void> {
    if (this.disposed || this.state === "starting") return;
    const generation = ++this.generation;
    this.setState("starting");
    let outcome: LspStartOutcome;
    try {
      outcome = await this.opts.start();
    } catch (err) {
      if (generation === this.generation) this.setState("crashed", err instanceof Error ? err.message : "start failed");
      return;
    }
    if (this.disposed || generation !== this.generation) {
      outcome.transport.close(); // superseded while starting: never leave a process behind
      return;
    }
    if (outcome.state === "unavailable") {
      outcome.transport.close();
      this.setState("unavailable", outcome.reason ?? "No language server available");
      return;
    }
    const transport = outcome.transport;
    const peer = new JsonRpcPeer(transport);
    this.transport = transport;
    this.peer = peer;
    this.sync = new DocumentSync(this.opts.projectId, { notify: (m, p) => peer.notify(m, p) });
    peer.onNotification("textDocument/publishDiagnostics", (p) => this.diagnostics.publish(p));
    peer.onRequest("workspace/configuration", (p) =>
      ((p as { items?: unknown[] } | null)?.items ?? []).map(() => null),
    );
    peer.onRequest("client/registerCapability", () => null);
    peer.onRequest("client/unregisterCapability", () => null);
    peer.onRequest("window/workDoneProgress/create", () => null);
    peer.onRequest("workspace/workspaceFolders", () => [
      { uri: `${LSP_SYNTHETIC_SCHEME}://${this.opts.projectId}`, name: this.opts.projectName },
    ]);
    peer.onRequest("workspace/applyEdit", async (p) => {
      const edit = (p as { edit?: LspWorkspaceEdit } | null)?.edit;
      const applied = edit && this.opts.applyEdit ? await this.opts.applyEdit(edit) : false;
      return { applied };
    });
    transport.onClose((reason) => {
      if (generation !== this.generation) return;
      this.peer = null;
      this.transport = null;
      this.sync?.reset();
      this.sync = null;
      this.diagnostics.clear();
      this.setState(reason === "crashed" || reason.startsWith("language server") ? "crashed" : "stopped", reason);
    });
    try {
      await peer.request("initialize", {
        processId: null,
        clientInfo: { name: "racler.dev" },
        rootUri: `${LSP_SYNTHETIC_SCHEME}://${this.opts.projectId}`,
        capabilities: CLIENT_CAPABILITIES,
        workspaceFolders: [{ uri: `${LSP_SYNTHETIC_SCHEME}://${this.opts.projectId}`, name: this.opts.projectName }],
      });
    } catch (err) {
      if (generation !== this.generation) return;
      this.teardown("initialize failed");
      this.setState("crashed", err instanceof Error ? err.message : "initialize failed");
      return;
    }
    if (generation !== this.generation) return;
    peer.notify("initialized", {});
    this.setState("ready");
    // Every attached document is opened exactly once in this fresh session.
    for (const doc of this.docs.values()) this.sync.open(doc);
  }
}
